import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { palette } from '@openx/components-core/lib/theme/paletteV2.ts';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue.ts';
import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { DistributionItem } from '../../data/chartTypes.ts';
import { getBarChartScales } from './getBarChartScales.ts';

const BAR_SIZE = 90;
const BAR_RADIUS = 4;
const TICK_STYLE = { fill: palette.text.secondary, fontSize: 12 };

const formatValue = (value: number) => abbreviateNumericValue(value, { allowZero: true, decimalPlaces: 1 });
const formatShare = (value: number) => `${Number(value.toFixed(1))}%`;

type BarTooltipProps = {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: DistributionItem }>;
};

const BarTooltip = ({ active, payload }: BarTooltipProps) => {
  const datum = payload?.[0]?.payload;
  if (!active || !datum) {
    return null;
  }

  return (
    <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${palette.divider}` }} data-test="insights-bar-chart-tooltip">
      <Typography variant="body1Bold">{datum.label}</Typography>
      <Typography variant="body1">
        {formatValue(datum.value)} ({formatShare(datum.share)})
      </Typography>
    </Paper>
  );
};

type InsightsBarChartProps = {
  title: string;
  data: DistributionItem[];
  isLoading?: boolean;
  isAnimationActive?: boolean;
};

export const InsightsBarChart = ({
  title,
  data,
  isLoading = false,
  isAnimationActive = true,
}: InsightsBarChartProps) => {
  const scales = useMemo(() => {
    const maxValue = data.reduce((max, { value }) => Math.max(max, value), 0);
    const total = data.reduce((sum, { value }) => sum + value, 0);
    return getBarChartScales(maxValue, total);
  }, [data]);

  const hasData = data.some(({ value }) => value > 0);

  const renderChart = () => {
    if (isLoading) {
      return <Skeleton animation="wave" variant="rectangular" height="100%" data-test="insights-bar-chart-loading" />;
    }

    if (!hasData) {
      return (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body1" sx={{ color: 'text.secondary' }} data-test="insights-bar-chart-no-data">
            No data available
          </Typography>
        </Box>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
          {/* Grid defaults to yAxisId 0, which doesn't exist here — bind it to the value axis or no lines render */}
          <CartesianGrid vertical={false} stroke={palette.divider} yAxisId="value" />
          <XAxis dataKey="label" axisLine={false} tickLine={false} tick={TICK_STYLE} />
          <YAxis
            yAxisId="value"
            orientation="left"
            domain={scales.value.domain}
            ticks={scales.value.ticks}
            tickFormatter={formatValue}
            axisLine={false}
            tickLine={false}
            tick={TICK_STYLE}
          />
          <YAxis
            yAxisId="share"
            orientation="right"
            // No series is bound to this axis — it mirrors the value axis, so force the precomputed domain
            allowDataOverflow
            domain={scales.share.domain}
            ticks={scales.share.ticks}
            tickFormatter={formatShare}
            axisLine={false}
            tickLine={false}
            tick={TICK_STYLE}
          />
          <Tooltip
            cursor={{ fill: palette.background.boxLight, opacity: 0.6 }}
            content={<BarTooltip />}
            // Default position animation slides the tooltip in from the chart corner on first hover
            isAnimationActive={false}
          />
          <Bar
            yAxisId="value"
            dataKey="value"
            maxBarSize={BAR_SIZE}
            radius={[BAR_RADIUS, BAR_RADIUS, 0, 0]}
            isAnimationActive={isAnimationActive}
          >
            {data.map(item => (
              <Cell key={item.key} fill={item.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Box data-test="insights-bar-chart" sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
      <Typography variant="h3" data-test="insights-bar-chart-title">
        {title}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>{renderChart()}</Box>
    </Box>
  );
};
